import React, { createContext, useState, useCallback, useEffect } from 'react';

// Single source of truth for the admin Prod | Test toggle.
// Persisted in localStorage and broadcast across tabs via the 'storage' event.
// The api interceptor in src/utils/api.js reads from localStorage directly
// so every admin request automatically includes the current mode as ?mode=...

export const DASHBOARD_MODE_KEY = 'admin.dashboardMode';
export const DASHBOARD_MODE_EVENT = 'admin:dashboardModeChanged';

export const DashboardModeContext = createContext({
  mode: 'prod',
  setMode: () => {},
});

const readInitialMode = () => {
  if (typeof window === 'undefined') return 'prod';
  const stored = window.localStorage.getItem(DASHBOARD_MODE_KEY);
  return stored === 'test' ? 'test' : 'prod';
};

export const DashboardModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readInitialMode);

  const setMode = useCallback((next) => {
    const safe = next === 'test' ? 'test' : 'prod';
    setModeState(safe);
    try {
      window.localStorage.setItem(DASHBOARD_MODE_KEY, safe);
      window.dispatchEvent(new CustomEvent(DASHBOARD_MODE_EVENT, { detail: safe }));
    } catch {
      // ignore
    }
  }, []);

  // Sync across tabs / windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === DASHBOARD_MODE_KEY) {
        setModeState(e.newValue === 'test' ? 'test' : 'prod');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <DashboardModeContext.Provider value={{ mode, setMode }}>
      {children}
    </DashboardModeContext.Provider>
  );
};

// Read the current mode without subscribing to React state.
// Used by the axios request interceptor.
export const getCurrentDashboardMode = () => {
  if (typeof window === 'undefined') return 'prod';
  const stored = window.localStorage.getItem(DASHBOARD_MODE_KEY);
  return stored === 'test' ? 'test' : 'prod';
};
